import { analytics } from '@lib/analytics/page-events';
import { GENESIS_PROVIDER_ID } from '@lib/api/auth/genesis-provider';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import { failingFetcher } from '@lib/http/fetch';
import { FetchError } from '@lib/types/api/FetchError';
import { ReviewsData } from '@lib/types/api/Review';
import { openInUnityEditor } from '@lib/util/unityEditor';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ProductReviewFilterValues,
  ProductTabValue,
  ReviewFormData,
} from 'ui-components';
import { formatDate } from 'utils';
import { useReviews } from '../../hooks/useReviews';
import {
  UpmProductDetailPagePropsActions,
  UpmProductDetailPagePropsData,
  UseUpmProductDetailPageProps,
} from './types';

export const useUpmProductDetailPage = (
  initialData: UpmProductDetailPagePropsData
): UseUpmProductDetailPageProps => {
  const router = useRouter();

  const { hasCookieConsent, openCookieSettings } = useCookieSettings();
  const [data, setData] = useState(initialData);
  const [shouldScrollToTab, setShouldScrollToTab] = useState(false);
  const hasTracked = useRef(false);

  const {
    onFilter: reviewsOnFilter,
    onSort: reviewsOnSort,
    onPageChange: reviewsOnPageChange,
    onSubmitReport: reviewsOnSubmitReport,
    onSubmitReview: reviewsOnSubmitReview,
    onDeleteReview: reviewsOnDeleteReview,
    onEditReview: reviewsOnEditReview,
    onEmailPublisher: reviewsOnEmailPublisher,
    onHelpfulVotesReview: reviewsOnHelpfulVotesReview,
    onSubmitReply: reviewsOnSubmitReply,
    onHelpfulVotesReply: reviewsOnHelpfulVotesReply,
    onEditReply: reviewsOnEditReply,
    onDeleteReply: reviewsOnDeleteReply,
  } = useReviews({
    productId: data.productId,
    publisherSupportContact: data.nutritionLabels.publisher.supportContact,
  });

  const scrollToTab = useCallback(() => {
    const productTabs = document.getElementById('product-tabs');

    if (productTabs) {
      productTabs.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const updateReviewsData = useCallback(
    (reviewsData: ReviewsData | undefined) => {
      if (reviewsData) {
        setData((prev) => {
          if (!prev.tabs?.reviews) return prev;

          return {
            ...prev,
            tabs: {
              ...prev.tabs,
              reviews: {
                ...prev.tabs.reviews,
                reviews: reviewsData.reviews,
                total: reviewsData.total,
                currentPage: reviewsData.currentPage,
                totalPages: reviewsData.totalPages,
                start: reviewsData.start,
                end: reviewsData.end,
                activeFilterValue: reviewsData.activeFilterValue as
                  | ProductReviewFilterValues
                  | undefined,
              },
            },
          };
        });
      }
    },
    []
  );

  const getTabFromHash = useCallback((): ProductTabValue => {
    const hash = window.location.hash.replace('#', '');
    const validTabs: ProductTabValue[] = ['overview', 'packages', 'releases'];

    return validTabs.includes(hash as ProductTabValue)
      ? (hash as ProductTabValue)
      : 'overview';
  }, []);

  useEffect(() => {
    if (shouldScrollToTab) {
      const timeoutId = setTimeout(() => {
        scrollToTab();
        setShouldScrollToTab(false);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [data.currentTab, shouldScrollToTab, scrollToTab]);

  useEffect(() => {
    const initialHash = getTabFromHash();
    if (initialHash !== 'overview') {
      setData((prev) => ({ ...prev, currentTab: initialHash }));
      setShouldScrollToTab(true);
    }

    const handleHashChange = () => {
      const newTab = getTabFromHash();

      setData((prev) => ({ ...prev, currentTab: newTab }));
      setShouldScrollToTab(true);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [getTabFromHash]);

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      gallery: {
        ...prev.gallery,
        hasCookieConsent,
      },
    }));
  }, [hasCookieConsent]);

  useEffect(() => {
    if (
      !hasTracked.current &&
      !data.previewMode &&
      data.productId &&
      data.productInfo?.productTitle
    ) {
      const price =
        !data.productInfo.price ||
        Object.keys(data.productInfo.price).length === 0
          ? '0.00'
          : data.productInfo.price.value;

      analytics.pushProductDetailsEvent({
        brand: data.productInfo.publisherName,
        category: data.productInfo.type,
        id: data.productId,
        name: data.productInfo.productTitle,
        price,
        variant: data.tabs.packages.packages[0]?.info.latestVersion,
      });

      analytics.pushProductsImpressionEvent([
        {
          brand: data.productInfo.publisherName,
          category: data.productInfo.type,
          id: data.productId,
          name: data.productInfo.productTitle,
          position: 1,
          price,
          variant: data.tabs.packages.packages[0]?.info.latestVersion,
        },
      ]);
      hasTracked.current = true;
    }
  }, [data, data.productId, data.productInfo, data.tabs.packages.packages]);

  const claimProduct = useCallback(async () => {
    try {
      await failingFetcher('/api/claim-product', {
        method: 'POST',
        body: JSON.stringify({
          productId: data.productId,
        }),
      });

      analytics.pushAddToMyAssetEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });

      setData((prev) => ({
        ...prev,
        wasEntitled: true,
        ownedDate: formatDate(new Date(), 'PP', prev.locale),
        productInfo: {
          ...prev.productInfo,
          isEntitled: true,
        },
        tabs: {
          ...prev.tabs,
          packages: {
            ...prev.tabs.packages,
            packages: prev.tabs.packages.packages.map((pck) => ({
              ...pck,
              isEntitled: true,
            })),
            reviews: {
              ...prev.tabs.reviews,
              reviews: prev.tabs?.reviews?.reviews.map((review) => ({
                ...review,
                value: {
                  ...review.reviewDetails,
                },
              })),
            },
          },
        },
      }));
    } catch (error) {
      if (error instanceof FetchError && error.status === 401) {
        const currentUrl = window.location.pathname + window.location.search;
        signIn(GENESIS_PROVIDER_ID, { callbackUrl: currentUrl });
        return;
      }
      console.error('Error claiming product:', error);
    }
  }, [data.productId, data.productInfo.productTitle]);

  const actions: UpmProductDetailPagePropsActions = {
    onClickProductInfo: useCallback(() => {
      if (!data.productInfo.isEntitled) {
        claimProduct();
      } else if (data.productInfo.type === 'single-package') {
        const packageId = data.tabs.packages.packages[0].id;
        analytics.pushOpenInUnityEvent({
          asset_id: packageId,
          asset_name: data.productInfo.productTitle,
        });
        openInUnityEditor(packageId);
      } else {
        setData((prev) => ({ ...prev, currentTab: 'packages' }));
        setShouldScrollToTab(true);
      }
    }, [
      data.productInfo.isEntitled,
      data.productInfo.type,
      data.productInfo.productTitle,
      data.tabs.packages.packages,
      claimProduct,
    ]),

    onOpenInUnityFromPackage: useCallback(
      (id: string) => {
        analytics.pushOpenInUnityEvent({
          asset_id: id,
          asset_name: data.productInfo.productTitle,
        });
        openInUnityEditor(id);
      },
      [data.productInfo.productTitle]
    ),

    onOpenInUnityFromEntitlement: useCallback(() => {
      const packageId = data.tabs.packages.packages[0].id;
      analytics.pushOpenInUnityEvent({
        asset_id: packageId,
        asset_name: data.productInfo.productTitle,
      });
      openInUnityEditor(packageId);
    }, [data.productInfo.productTitle, data.tabs.packages.packages]),

    onGoToMyAssets: useCallback(() => {
      router.push('/account/assets/upm');
    }, [router]),

    onCloseFromEntitlement: useCallback(() => {
      setData((prev) => ({ ...prev, wasEntitled: false }));
    }, []),

    onSeeOwnedPackages: useCallback(() => {
      setData((prev) => ({ ...prev, currentTab: 'packages' }));
      setShouldScrollToTab(true);
    }, []),

    onClickChipList: useCallback(() => {
      console.log('Chip list clicked');
    }, []),

    onHandleTabChange: useCallback((tab: ProductTabValue) => {
      setData((prev) => ({ ...prev, currentTab: tab }));
    }, []),

    onOpenCookieSettings: openCookieSettings,

    onAccordionChange: useCallback((name: string, action: 'open' | 'close') => {
      analytics.pushProductAccordionClickEvent({
        action_name: name,
        action,
      });
    }, []),

    // Reviews actions
    onDeleteReview: useCallback(
      async (reviewId: string) => {
        const reviewsData = await reviewsOnDeleteReview(reviewId);
        updateReviewsData(reviewsData);
      },
      [reviewsOnDeleteReview, updateReviewsData]
    ),
    onFilterReviews: useCallback(
      async (filter: number) => {
        const filterStr = filter === 0 ? '' : filter.toString();
        setShouldScrollToTab(true);
        const reviewsData = await reviewsOnFilter(filterStr);
        updateReviewsData(reviewsData);
      },
      [reviewsOnFilter, updateReviewsData]
    ),
    onEditReview: useCallback(
      async (reviewId: string, formData: ReviewFormData) => {
        const reviewsData = await reviewsOnEditReview(reviewId, formData);
        updateReviewsData(reviewsData);
      },
      [reviewsOnEditReview, updateReviewsData]
    ),
    onFilter: useCallback(
      async (filter: string) => {
        setShouldScrollToTab(true);
        const reviewsData = await reviewsOnFilter(filter);
        updateReviewsData(reviewsData);
      },
      [reviewsOnFilter, updateReviewsData]
    ),
    onSort: useCallback(
      async (sort: string) => {
        setShouldScrollToTab(true);
        const reviewsData = await reviewsOnSort(sort);
        updateReviewsData(reviewsData);
      },
      [reviewsOnSort, updateReviewsData]
    ),
    onPageChange: useCallback(
      async (page: number) => {
        setShouldScrollToTab(true);
        const reviewsData = await reviewsOnPageChange(page);
        updateReviewsData(reviewsData);
        return reviewsData;
      },
      [reviewsOnPageChange, updateReviewsData]
    ),
    onSubmitReport: useCallback(
      async (reviewId: string, report: { reportReview: string }) => {
        await reviewsOnSubmitReport(reviewId, data.productId, report);
      },
      [reviewsOnSubmitReport, data.productId]
    ),
    onEmailPublisher: reviewsOnEmailPublisher,
    onSubmitReview: reviewsOnSubmitReview,

    // TODO: These features are not going to be part of the GA launch
    onHelpfulVotesReview: reviewsOnHelpfulVotesReview,
    onSubmitReply: reviewsOnSubmitReply,
    onHelpfulVotesReply: reviewsOnHelpfulVotesReply,
    onEditReply: reviewsOnEditReply,
    onDeleteReply: reviewsOnDeleteReply,
  };

  return {
    data,
    actions,
  };
};
